import { useMemo, useState } from "react";

import type { BusyPost } from "./ScheduleStrip";
import { timezoneLabel } from "../lib/tz";

// One control for the whole "when" decision: a Publish-now / Schedule toggle,
// and — when scheduling — a single panel that IS both the calendar and the time
// picker. Value contract matches the rest of the composer: a wall-clock
// "YYYY-MM-DDTHH:mm" string (interpreted in the user's timezone by the composer),
// or "" for publish-now. Day/clock math is browser-local to stay consistent with
// the value string (which is wall-clock), same as the old strip.

const PRESETS: { t: string; label: string }[] = [
  { t: "09:00", label: "9:00 AM" },
  { t: "12:00", label: "12:00 PM" },
  { t: "15:00", label: "3:00 PM" },
  { t: "18:00", label: "6:00 PM" },
  { t: "21:00", label: "9:00 PM" },
];
const DEFAULT_TIME = "18:00";
const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const CLASH_MIN = 15;
const MINUTE = 60_000;

const pad = (n: number) => String(n).padStart(2, "0");
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function label12(t: string): string {
  const [h, m] = t.split(":").map(Number);
  const ap = (h ?? 0) >= 12 ? "PM" : "AM";
  const hh = (h ?? 0) % 12 || 12;
  return `${hh}:${pad(m ?? 0)} ${ap}`;
}

/** Soonest sensible default when the user switches into Schedule mode: the next
 * preset still in the future today, else tomorrow at the default time. */
function defaultDateTime(now: Date): string {
  const nowMin = now.getHours() * 60 + now.getMinutes();
  for (const p of PRESETS) {
    const [h, m] = p.t.split(":").map(Number);
    if ((h ?? 0) * 60 + (m ?? 0) > nowMin + 5) return `${ymd(now)}T${p.t}`;
  }
  const tm = new Date(now);
  tm.setDate(tm.getDate() + 1);
  return `${ymd(tm)}T${DEFAULT_TIME}`;
}

export function SchedulePicker({
  value,
  onChange,
  busy,
  timezone,
}: {
  /** Wall-clock "YYYY-MM-DDTHH:mm", or "" for publish now. */
  value: string;
  onChange: (v: string) => void;
  busy: BusyPost[];
  /** Effective IANA timezone, for the "times are in …" note. */
  timezone?: string;
}) {
  const now = new Date();
  const todayStr = ymd(now);
  const scheduled = Boolean(value);
  const dateStr = value ? value.slice(0, 10) : "";
  const timeStr = value ? value.slice(11, 16) : DEFAULT_TIME;

  const seed = dateStr ? new Date(`${dateStr}T00:00`) : now;
  const [view, setView] = useState({ y: seed.getFullYear(), m: seed.getMonth() });

  // Days that already have a post, for a subtle "booked" dot.
  const busyDays = useMemo(() => {
    const s = new Set<string>();
    for (const b of busy) s.add(ymd(new Date(b.at)));
    return s;
  }, [busy]);

  const selectedMs = value ? new Date(value).getTime() : NaN;
  const clash =
    Number.isFinite(selectedMs)
      ? busy.find((b) => Math.abs(b.at - selectedMs) <= CLASH_MIN * MINUTE)
      : undefined;

  function shiftMonth(delta: number) {
    setView((v) => {
      const d = new Date(v.y, v.m + delta, 1);
      return { y: d.getFullYear(), m: d.getMonth() };
    });
  }
  function goSchedule() {
    const def = defaultDateTime(now);
    setView({ y: Number(def.slice(0, 4)), m: Number(def.slice(5, 7)) - 1 });
    onChange(def);
  }
  const pickDay = (d: string) => onChange(`${d}T${timeStr}`);
  const pickTime = (t: string) => onChange(`${dateStr || todayStr}T${t}`);

  // Leading blanks + day numbers for the month grid.
  const startPad = new Date(view.y, view.m, 1).getDay();
  const daysInMonth = new Date(view.y, view.m + 1, 0).getDate();
  const cells: (number | null)[] = [
    ...Array.from({ length: startPad }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  return (
    <div className="sched2">
      <div className="sched2-modes" role="group" aria-label="When to publish">
        <button
          type="button"
          className={`sched2-mode${!scheduled ? " is-active" : ""}`}
          aria-pressed={!scheduled}
          onClick={() => onChange("")}
        >
          <span className="sched2-mode-dot" aria-hidden="true" />
          Publish now
        </button>
        <button
          type="button"
          className={`sched2-mode${scheduled ? " is-active" : ""}`}
          aria-pressed={scheduled}
          onClick={() => {
            if (!scheduled) goSchedule();
          }}
        >
          <span className="sched2-mode-cal" aria-hidden="true">📅</span>
          Schedule
        </button>
      </div>

      {scheduled && (
        <div className="sched2-panel">
          <div className="sched2-cal-head">
            <button
              type="button"
              className="sched2-nav"
              aria-label="Previous month"
              onClick={() => shiftMonth(-1)}
            >
              ‹
            </button>
            <strong aria-live="polite">
              {MONTHS[view.m]} {view.y}
            </strong>
            <button
              type="button"
              className="sched2-nav"
              aria-label="Next month"
              onClick={() => shiftMonth(1)}
            >
              ›
            </button>
          </div>

          <div className="sched2-weekdays" aria-hidden="true">
            {WEEKDAYS.map((w, i) => (
              <span key={i}>{w}</span>
            ))}
          </div>

          <div className="sched2-grid" role="grid" aria-label="Choose a day">
            {cells.map((cell, i) =>
              cell === null ? (
                <span key={`e${i}`} className="sched2-cell is-empty" />
              ) : (
                (() => {
                  const cStr = `${view.y}-${pad(view.m + 1)}-${pad(cell)}`;
                  const past = cStr < todayStr;
                  const isSel = cStr === dateStr;
                  const isToday = cStr === todayStr;
                  const isBusy = busyDays.has(cStr);
                  return (
                    <button
                      key={cStr}
                      type="button"
                      className={`sched2-cell${isSel ? " is-selected" : ""}${
                        isToday ? " is-today" : ""
                      }`}
                      aria-pressed={isSel}
                      aria-current={isToday ? "date" : undefined}
                      disabled={past}
                      onClick={() => pickDay(cStr)}
                    >
                      {cell}
                      {isBusy && <span className="sched2-busy-dot" aria-hidden="true" />}
                    </button>
                  );
                })()
              ),
            )}
          </div>

          <div className="sched2-time">
            <span className="sched2-time-label">Time</span>
            <div className="sched2-time-chips">
              {PRESETS.map((p) => (
                <button
                  key={p.t}
                  type="button"
                  className={`sched2-chip${timeStr === p.t ? " is-active" : ""}`}
                  aria-pressed={timeStr === p.t}
                  onClick={() => pickTime(p.t)}
                >
                  {p.label}
                </button>
              ))}
              <input
                type="time"
                className="sched2-time-input"
                aria-label="Custom time"
                value={timeStr}
                onChange={(e) => e.target.value && pickTime(e.target.value)}
              />
            </div>
          </div>

          {clash && (
            <p className="muted small warn sched2-clash">
              ⚠ You already have a post around {label12(new Date(clash.at).toTimeString().slice(0, 5))}{" "}
              that day — this one would go out on top of it.
            </p>
          )}
        </div>
      )}

      {timezone && (
        <span className="muted small sched2-tz">
          Times are in {timezoneLabel(timezone)} — change it in Settings.
        </span>
      )}
    </div>
  );
}
